import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const EnhancedLegend = ({ 
  data = [], 
  dataKey = 'value', 
  nameKey = 'name',
  colors = [
    'var(--accent)',
    'var(--status-good)',
    'var(--status-warn)',
    'var(--status-bad)',
    '#8B5CF6',
    '#06B6D4',
    '#84CC16',
    '#F97316'
  ],
  maxItems = 5,
  showToggle = true,
  className = '',
  sortDescending = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0);
  
  // Ordenar dados por valor (maior para menor por padrão)
  const sortedData = [...data].sort((a, b) => {
    const aValue = parseFloat(a[dataKey] || 0);
    const bValue = parseFloat(b[dataKey] || 0);
    return sortDescending ? bValue - aValue : aValue - bValue;
  });
  
  const visibleItems = isExpanded ? sortedData : sortedData.slice(0, maxItems);
  const hasMoreItems = sortedData.length > maxItems;

  return (
    <div className={`space-y-2 ${className}`}>
      {visibleItems.map((item, index) => {
        const percentage = total > 0 ? ((item[dataKey] / total) * 100).toFixed(1) : 0;
        const originalIndex = data.findIndex(d => d === item);
        
        return (
          <div 
            key={index} 
            className="group flex items-center space-x-3 p-3 bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)]"
          >
            {/* Indicador de cor */}
            <div 
              className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm ring-2 ring-[color:var(--surface)]" 
              style={{ backgroundColor: colors[originalIndex % colors.length] }}
            />
            
            {/* Conteúdo principal */}
            <div className="flex-1 min-w-0">
              <div 
                className="font-medium text-[color:var(--text-primary)] text-sm leading-tight break-words" 
                title={item[nameKey]}
              >
                {item[nameKey]}
              </div>
            </div>
            
            {/* Valores */}
            <div className="flex flex-col items-end ml-3 space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-[color:var(--text-primary)] font-semibold text-sm">
                  {percentage}%
                </span>
                <div className="w-10 h-2 bg-[color:var(--surface)] rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-300"
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: colors[originalIndex % colors.length]
                    }}
                  />
                </div>
              </div>
              <span className="text-xs text-[color:var(--text-muted)] font-mono">
                {item[dataKey].toLocaleString('pt-BR')}
              </span>
            </div>
          </div>
        );
      })}
      
      {/* Botão para expandir/recolher */}
      {hasMoreItems && showToggle && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-center space-x-2 p-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] rounded-lg transition-colors"
      >
          <span>
            {isExpanded ? 'Mostrar menos' : `Mostrar mais (${data.length - maxItems} itens)`}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      )}
      
      {/* Resumo quando recolhido */}
      {!isExpanded && hasMoreItems && (
        <div className="text-xs text-[color:var(--text-muted)] text-center py-2 border-t border-[color:var(--border)]">
          +{sortedData.length - maxItems} itens adicionais
        </div>
      )}
    </div>
  );
};

export default EnhancedLegend;
